import type {
  DefinicijaKazalnika,
  EnotaKazalnika,
  FormulaKazalnika,
  Izraz,
  LetoPodatkov,
  MrezaGraditelja,
  ObdobjeSklica,
  OdsekGraditelja,
  PredogledFormule,
  Podjetje,
  SplosneNastavitveKazalnikov,
} from '@koda/shared';
import { vozliscaIzraza, VZOREC_KODE_KAZALNIKA } from '@koda/shared';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type JSX } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { api } from '../lib/api';
import { znesek } from '../lib/format';
import { celiceFormule, NapakaFormule, razcleniFormulo, vBesedilo, vIzraz, type DrevoFormule, type Stolpec } from '../lib/formula';
import {
  ostevilci,
  prevediKoordinate,
  sklicStevilke,
  stevilkaSklica,
  type OstevilcenaVrstica,
  type Stevilcenje,
} from '../lib/graditelj';
import { useBesedila, type Besedila } from '../lib/jezik';
import { PanelRazclenitve, type Celica } from '../sestavni/PanelRazclenitve';
import { IzracunKazalnika } from '../sestavni/PregledKazalnikov';

const ODSEKI: OdsekGraditelja[] = ['ipi', 'bs', 'cf', 'vloge', 'izracunane'];
const STOLPCI: { stolpec: Stolpec; obdobje: ObdobjeSklica }[] = [
  { stolpec: 'A', obdobje: 'current' },
  { stolpec: 'B', obdobje: 'prior_same_period' },
  { stolpec: 'C', obdobje: 'prior_year_end' },
];
const ENOTE: EnotaKazalnika[] = ['odstotek', 'krat', 'meseci', 'eur'];
/** Čas med zadnjim tipkanjem in predogledom: ne vsaka črka svoja zahteva. */
const ZAMIK_PREDOGLEDA = 400;

/** Privzeta skupina novega kazalnika (na dnu strani Pregled). */
const SKUPINA_PO_MERI = 'po_meri';

/**
 * Koda iz naziva: male črke brez šumnikov, podčrtaj namesto presledkov in
 * ločil. Vedno ustreza `VZOREC_KODE_KAZALNIKA`, po katerem jo preveri
 * strežnik: začne se s črko in ima najmanj dva znaka (»A« → »k_a«, prazen
 * naziv → »k_kazalnik«).
 */
export function kodaIzNaziva(naziv: string): string {
  const k = naziv
    .toLocaleLowerCase('sl')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  if (k === '') return 'k_kazalnik';
  const zCrko = /^[a-z]/.test(k) && k.length >= 2 ? k : `k_${k}`;
  return zCrko.slice(0, 50).replace(/_+$/, '');
}

/** Podatki za shranjevanje v katalog. */
interface Obrazec {
  koda: string;
  /** Kodo je skrbnik vpisal sam; sicer nastane iz naziva. */
  kodaRocno: boolean;
  nazivSl: string;
  nazivEn: string;
  opisSl: string;
  opisEn: string;
  skupina: string;
  dodajVPregled: boolean;
}

const PRAZEN_OBRAZEC: Obrazec = {
  koda: '',
  kodaRocno: false,
  nazivSl: '',
  nazivEn: '',
  opisSl: '',
  opisEn: '',
  skupina: SKUPINA_PO_MERI,
  dodajVPregled: false,
};

/** Iskanje brez razlike med velikimi in malimi črkami ter šumniki (č = c). */
const zaIskanje = (s: string): string =>
  s
    .toLocaleLowerCase('sl')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

/** Izid razčlenitve besedila: drevo in drevo izraza ali napaka v jeziku uporabnika. */
interface IzidFormule {
  drevo: DrevoFormule | null;
  izraz: Izraz | null;
  napaka: string | null;
}

function preberiFormulo(besedilo: string, s: Stevilcenje | null, b: Besedila): IzidFormule {
  let drevo: DrevoFormule | null;
  try {
    drevo = razcleniFormulo(besedilo);
  } catch (e) {
    if (e instanceof NapakaFormule) return { drevo: null, izraz: null, napaka: b.gr.napaka(e.vrsta, e.mesto, e.podrobnost) };
    throw e;
  }
  if (drevo === null || s === null) return { drevo, izraz: null, napaka: null };
  try {
    return { drevo, izraz: vIzraz(drevo, (st) => sklicStevilke(s, st)), napaka: null };
  } catch (e) {
    if (e instanceof NapakaFormule) return { drevo, izraz: null, napaka: b.gr.napaka(e.vrsta, e.mesto, e.podrobnost) };
    throw e;
  }
}

/**
 * Graditelj kazalnikov (docs/ARHITEKTURA.md, 6.3 – Graditelj). Samo skrbnik.
 *
 * Levo formula in predogled, desno mreža vrstic standardne sheme s stolpci
 * A (tekoče obdobje), B (isto obdobje primerjalnega leta) in C (konec
 * primerjalnega leta). Celica ni izračunana v brskalniku: vrednosti mreže in
 * predogled izračuna strežnik z istim motorjem kot stran Pregled. Podjetje je
 * samo za predogled; formula se veže na stalne ključe sheme, ne na vrstice
 * podjetja.
 */
export function GraditeljKazalnikov(): JSX.Element {
  const b = useBesedila();
  const [iskalni, setIskalni] = useSearchParams();
  const [podjetja, setPodjetja] = useState<Podjetje[] | null>(null);
  const [leta, setLeta] = useState<LetoPodatkov[] | null>(null);
  const [mreza, setMreza] = useState<MrezaGraditelja | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);

  const podjetje = iskalni.get('podjetje') ?? podjetja?.[0]?.id ?? null;
  const letoIzNaslova = Number(iskalni.get('leto'));
  const primerjalnoIzNaslova = Number(iskalni.get('primerjalno'));

  // Formula in prikaz.
  const [besedilo, setBesedilo] = useState('');
  const [enota, setEnota] = useState<EnotaKazalnika>('odstotek');
  const [decimalke, setDecimalke] = useState(1);
  const [imenovalecPozitiven, setImenovalecPozitiven] = useState(false);
  const [skrijNic, setSkrijNic] = useState(false);
  const [projekcijaOd, setProjekcijaOd] = useState(1);
  const vnos = useRef<HTMLInputElement>(null);
  /** Kam postaviti kazalko po vpisu celice (uporabi se pred izrisom). */
  const kazalka = useRef<number | null>(null);

  // Mreža.
  const [pokaziC, setPokaziC] = useState(false);
  const [zavihek, setZavihek] = useState<OdsekGraditelja>('ipi');
  const [iskanje, setIskanje] = useState('');
  const [razprte, setRazprte] = useState<Set<string>>(new Set());

  // Predogled.
  const [predogled, setPredogled] = useState<PredogledFormule | null>(null);
  const [napakaPredogleda, setNapakaPredogleda] = useState<string | null>(null);
  const [racunam, setRacunam] = useState(false);
  const [celica, setCelica] = useState<Celica | null>(null);

  // Shranjevanje v katalog: nov kazalnik, urejanje (?uredi=koda) ali kopija
  // vgrajenega (?kopija=koda). Parametra se prebereta enkrat ob odprtju.
  const [zacetek] = useState(() => ({ uredi: iskalni.get('uredi'), kopija: iskalni.get('kopija') }));
  const [katalog, setKatalog] = useState<SplosneNastavitveKazalnikov | null>(null);
  const [obrazec, setObrazec] = useState<Obrazec>(PRAZEN_OBRAZEC);
  /** Koda kazalnika, ki se ureja; `null` pri novem (tudi pri kopiji). */
  const [urejana, setUrejana] = useState<string | null>(zacetek.uredi);
  const [vrstniRed, setVrstniRed] = useState<number | undefined>(undefined);
  /** Shranjena formula, ki čaka na mrežo, da se zapiše s koordinatami. */
  const [nalozi, setNalozi] = useState<Izraz | null>(null);
  const [napakaNalaganja, setNapakaNalaganja] = useState<string | null>(null);
  const [shranjujem, setShranjujem] = useState(false);
  /** Zadnje uspešno shranjevanje; `formula` je zapis, ki je šel na strežnik. */
  const [shranjeno, setShranjeno] = useState<{ naziv: string; opozorila: string[]; formula: string } | null>(null);
  const [napakaShranjevanja, setNapakaShranjevanja] = useState<string | null>(null);
  const spremeni = (s: Partial<Obrazec>): void => {
    setObrazec((o) => ({ ...o, ...s }));
    setShranjeno(null);
  };
  const koda = obrazec.kodaRocno ? obrazec.koda : kodaIzNaziva(obrazec.nazivSl);
  const kodaVeljavna = urejana !== null || VZOREC_KODE_KAZALNIKA.test(koda);

  const nastavi = (spremembe: Record<string, string | null>): void => {
    const nov = new URLSearchParams(iskalni);
    for (const [k, v] of Object.entries(spremembe)) {
      if (v === null) nov.delete(k);
      else nov.set(k, v);
    }
    setIskalni(nov, { replace: true });
  };

  useEffect(() => {
    api
      .splosneNastavitveKazalnikov()
      .then((k) => {
        setKatalog(k);
        const koda = zacetek.uredi ?? zacetek.kopija;
        if (koda === null) return;
        const kazalnik = k.kazalniki.find((x) => x.koda === koda);
        if (kazalnik === undefined) {
          setNapakaNalaganja(b.gr.niKazalnika(koda));
          return;
        }
        if (kazalnik.formulaZapis === null) {
          setNapakaNalaganja(b.gr.brezFormule);
          return;
        }
        const f = kazalnik.formulaZapis;
        const kopija = zacetek.uredi === null;
        setObrazec({
          koda: kopija ? '' : kazalnik.koda,
          kodaRocno: !kopija,
          nazivSl: kopija ? b.gr.kopija(kazalnik.privzeto.nazivSl) : kazalnik.privzeto.nazivSl,
          nazivEn: kopija ? `${kazalnik.privzeto.nazivEn} (copy)` : kazalnik.privzeto.nazivEn,
          opisSl: kazalnik.privzeto.opisSl,
          opisEn: kazalnik.privzeto.opisEn,
          skupina: kopija ? SKUPINA_PO_MERI : kazalnik.skupina,
          dodajVPregled: kopija ? false : kazalnik.privzeto.vklopljen,
        });
        if (!kopija) setVrstniRed(kazalnik.privzeto.vrstniRed);
        setEnota(f.display.unit);
        setDecimalke(f.display.decimals);
        setImenovalecPozitiven(f.divisor_positive === true);
        setSkrijNic(f.hide_when_zero === true);
        setProjekcijaOd(f.projection_from_month ?? 1);
        setNalozi(f.expression);
      })
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : String(e));
      });
    // Samo ob odprtju: po shranjevanju nove kode se podatki ne naložijo znova.
  }, []);

  useEffect(() => {
    api
      .podjetja()
      .then(setPodjetja)
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : String(e));
      });
  }, []);

  useEffect(() => {
    if (podjetje === null) return;
    // Ob hitri menjavi podjetja odgovor prejšnjega ne sme povoziti let novega.
    let velja = true;
    setLeta(null);
    api
      .leta(podjetje)
      .then((l) => {
        if (velja) setLeta(l);
      })
      .catch((e: unknown) => {
        if (velja) setNapaka(e instanceof Error ? e.message : String(e));
      });
    return () => {
      velja = false;
    };
  }, [podjetje]);

  // Leti: iz naslova, sicer najnovejše s knjižbami in leto prej.
  const najnovejse = leta === null || leta.length === 0 ? null : Math.max(...leta.map((l) => l.leto));
  const leto = leta !== null && leta.some((l) => l.leto === letoIzNaslova) ? letoIzNaslova : najnovejse;
  const primerjalno =
    leto === null ? null : Number.isInteger(primerjalnoIzNaslova) && primerjalnoIzNaslova > 0 && primerjalnoIzNaslova < leto ? primerjalnoIzNaslova : leto - 1;

  useEffect(() => {
    if (podjetje === null || leto === null || primerjalno === null) return;
    let velja = true;
    setMreza(null);
    setNapaka(null);
    api
      .graditelj(podjetje, leto, primerjalno)
      .then((m) => {
        if (velja) setMreza(m);
      })
      .catch((e: unknown) => {
        if (velja) setNapaka(e instanceof Error ? e.message : String(e));
      });
    return () => {
      velja = false;
    };
  }, [podjetje, leto, primerjalno]);

  const stevilcenje = useMemo(() => (mreza === null ? null : ostevilci(mreza)), [mreza]);

  // Besedilo formule velja za številčenje, s katerim je bilo napisano. Ko
  // pride nova mreža (drugo podjetje ali leto), se koordinate prevedejo prek
  // pomena celic, preden se formula znova prebere – sicer bi »A5« pri drugem
  // podjetju lahko pomenil drugo vrstico. Prevod je med izrisom (ne v
  // učinku), zato nikoli ne gre na strežnik formula s spremenjenim pomenom.
  const [osnova, setOsnova] = useState<Stevilcenje | null>(null);
  const [neprenesena, setNeprenesena] = useState<string | null>(null);
  if (stevilcenje !== null && stevilcenje !== osnova) {
    if (osnova !== null && besedilo.trim() !== '') {
      const prevod = prevediKoordinate(besedilo, osnova, stevilcenje);
      if (prevod === null) {
        setNeprenesena(besedilo);
        setBesedilo('');
      } else if (prevod !== besedilo) {
        setBesedilo(prevod);
      }
    }
    setOsnova(stevilcenje);
  }
  // Shranjena formula se zapiše s koordinatami te mreže (prek pomena celic).
  // Če katere vrstice v mreži ni, formula čaka (drugo podjetje jo morda ima).
  if (nalozi !== null && stevilcenje !== null && stevilcenje === osnova) {
    const zapis = vBesedilo(nalozi, (sk) => stevilkaSklica(stevilcenje, sk));
    if (zapis === null) {
      if (napakaNalaganja !== b.gr.niVMrezi) setNapakaNalaganja(b.gr.niVMrezi);
    } else {
      setBesedilo(zapis);
      setNalozi(null);
      setNapakaNalaganja(null);
    }
  }
  const formula = useMemo(() => preberiFormulo(besedilo, stevilcenje, b), [besedilo, stevilcenje, b]);
  const uporabljene = useMemo(() => celiceFormule(formula.drevo), [formula.drevo]);
  const zStolpcemC = pokaziC || uporabljene.some((c) => c.stolpec === 'C');
  const zProjekcijo =
    formula.izraz !== null && [...vozliscaIzraza(formula.izraz)].some((v) => v.op === 'ref' && v.aggregation === 'annualized_ytd');

  const zapisFormule: FormulaKazalnika | null =
    formula.izraz === null
      ? null
      : {
          result: 'scalar',
          expression: formula.izraz,
          display: { unit: enota, decimals: decimalke },
          ...(imenovalecPozitiven ? { divisor_positive: true } : {}),
          ...(skrijNic ? { hide_when_zero: true } : {}),
          ...(zProjekcijo && projekcijaOd > 1 ? { projection_from_month: projekcijaOd } : {}),
        };
  const kljucPredogleda = zapisFormule === null ? null : JSON.stringify(zapisFormule);

  // Predogled: z zamikom po zadnji spremembi; zastarel odgovor se zavrže.
  useEffect(() => {
    if (kljucPredogleda === null || podjetje === null || leto === null || primerjalno === null) {
      setPredogled(null);
      setNapakaPredogleda(null);
      setRacunam(false);
      return;
    }
    let velja = true;
    setRacunam(true);
    const cas = setTimeout(() => {
      api
        .predogledFormule(podjetje, JSON.parse(kljucPredogleda) as FormulaKazalnika, leto, primerjalno)
        .then((p) => {
          if (!velja) return;
          setPredogled(p);
          setNapakaPredogleda(null);
        })
        .catch((e: unknown) => {
          if (!velja) return;
          setPredogled(null);
          setNapakaPredogleda(e instanceof Error ? e.message : String(e));
        })
        .finally(() => {
          if (velja) setRacunam(false);
        });
    }, ZAMIK_PREDOGLEDA);
    return () => {
      velja = false;
      clearTimeout(cas);
    };
  }, [kljucPredogleda, podjetje, leto, primerjalno]);

  /** Shrani kazalnik v katalog (nov ali sprememba); strežnik vse preveri znova. */
  const shrani = (): void => {
    if (zapisFormule === null || kljucPredogleda === null) return;
    const shranjenaFormula = kljucPredogleda;
    const definicija: DefinicijaKazalnika = {
      ...(urejana === null ? { koda } : {}),
      skupina: obrazec.skupina,
      ...(vrstniRed === undefined ? {} : { vrstniRed }),
      privzetoVklopljen: obrazec.dodajVPregled,
      nazivSl: obrazec.nazivSl,
      nazivEn: obrazec.nazivEn,
      opisSl: obrazec.opisSl,
      opisEn: obrazec.opisEn,
      formula: zapisFormule,
    };
    setShranjujem(true);
    setShranjeno(null);
    setNapakaShranjevanja(null);
    api
      .shraniDefinicijo(urejana, definicija)
      .then((izid) => {
        if (urejana === null) {
          // Nov kazalnik je shranjen: nadaljnja shranjevanja ga spreminjajo.
          setUrejana(izid.koda);
          setObrazec((o) => ({ ...o, koda: izid.koda, kodaRocno: true }));
          nastavi({ uredi: izid.koda, kopija: null });
        }
        setShranjeno({ naziv: obrazec.nazivSl, opozorila: izid.opozorila, formula: shranjenaFormula });
      })
      .catch((e: unknown) => {
        setNapakaShranjevanja(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setShranjujem(false);
      });
  };

  /**
   * Skrbnik je začel formulo pisati sam: shranjena formula, ki v tej mreži ni
   * predstavljiva, ne čaka več – sicer bi ob menjavi podjetja povozila vnos.
   */
  const opustiNalaganje = (): void => {
    if (nalozi === null) return;
    setNalozi(null);
    setNapakaNalaganja(null);
  };

  /** Vpiše koordinato na mesto kazalke (ali namesto izbranega besedila). */
  const vpisi = (koordinata: string): void => {
    const polje = vnos.current;
    const zacetek = polje?.selectionStart ?? besedilo.length;
    const konec = polje?.selectionEnd ?? besedilo.length;
    const osnova = besedilo.trim() === '' ? '=' : besedilo;
    const od = besedilo.trim() === '' ? 1 : zacetek;
    const do_ = besedilo.trim() === '' ? 1 : konec;
    const novo = `${osnova.slice(0, od)}${koordinata}${osnova.slice(do_)}`;
    kazalka.current = od + koordinata.length;
    setBesedilo(novo);
    setNeprenesena(null);
    opustiNalaganje();
    polje?.focus();
  };

  // Kazalka za vpisano celico, preden brskalnik izriše – nadaljnje tipkanje
  // gre takoj za oznako celice.
  useLayoutEffect(() => {
    const mesto = kazalka.current;
    if (mesto === null) return;
    kazalka.current = null;
    vnos.current?.setSelectionRange(mesto, mesto);
  }, [besedilo]);

  return (
    <main className="siroko">
      <div className="vsebina graditelj">
        <nav className="drobtine">
          <span>{b.sn.splosneNastavitve}</span>
          <Link to="/nastavitve/kazalniki">{b.gr.nazaj}</Link>
          <span>{b.gr.naslov}</span>
        </nav>
        <h1>{b.gr.naslov}</h1>
        {urejana !== null && <p className="drobno">{b.gr.urejam(obrazec.nazivSl || urejana)}</p>}
        <p className="podnaslov">{b.gr.podnaslov}</p>
        {napaka !== null && <p className="napaka">{napaka}</p>}
        {napakaNalaganja !== null && (
          <p className="napaka" role="alert">
            {napakaNalaganja}
          </p>
        )}

        <IzbiraPredogleda
          podjetja={podjetja}
          podjetje={podjetje}
          leta={leta}
          leto={leto}
          primerjalno={primerjalno}
          mreza={mreza}
          pokaziC={zStolpcemC}
          naPodjetje={(id) => {
            nastavi({ podjetje: id, leto: null, primerjalno: null });
          }}
          naLeto={(l) => {
            nastavi({ leto: String(l), primerjalno: null });
          }}
          naPrimerjalno={(l) => {
            nastavi({ primerjalno: String(l) });
          }}
          naStolpecC={setPokaziC}
        />

        <div className="graditelj-postavitev">
          <div className="graditelj-levo">
            <section className="graditelj-plosca" aria-labelledby="naslov-formule">
              <h2 id="naslov-formule">{b.gr.formula}</h2>
              <div className="formula-vrstica">
                <span className="fx" aria-hidden="true">
                  fx
                </span>
                <input
                  ref={vnos}
                  className="mono"
                  aria-labelledby="naslov-formule"
                  spellCheck={false}
                  autoComplete="off"
                  placeholder={b.gr.placeholder}
                  value={besedilo}
                  onChange={(e) => {
                    setBesedilo(e.target.value);
                    setNeprenesena(null);
                    opustiNalaganje();
                  }}
                />
              </div>
              {neprenesena !== null && (
                <p className="opozorilo" role="status">
                  {b.gr.neprenesena(neprenesena)}
                </p>
              )}
              {formula.napaka !== null && (
                <p className="napaka" role="alert">
                  {formula.napaka}
                </p>
              )}
              <PomenCelic uporabljene={uporabljene} stevilcenje={stevilcenje} mreza={mreza} />
              <p className="namig">{b.gr.namigFormule}</p>

              <h3 className="drobno">{b.gr.prikaz}</h3>
              <div className="mreza-obrazca">
                <label className="polje">
                  {b.gr.enota}
                  <select
                    value={enota}
                    onChange={(e) => {
                      setEnota(e.target.value as EnotaKazalnika);
                    }}
                  >
                    {ENOTE.map((e) => (
                      <option key={e} value={e}>
                        {b.gr.enote[e]}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="polje">
                  {b.gr.decimalke}
                  <select
                    value={decimalke}
                    onChange={(e) => {
                      setDecimalke(Number(e.target.value));
                    }}
                  >
                    {[0, 1, 2, 3, 4].map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </label>
                {zProjekcijo && (
                  <label className="polje">
                    {b.gr.projekcijaOd}
                    <select
                      value={projekcijaOd}
                      onChange={(e) => {
                        setProjekcijaOd(Number(e.target.value));
                      }}
                    >
                      {b.meseci.map((m, i) => (
                        <option key={m} value={i + 1}>
                          {m}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
              <label className="stikalo">
                <input
                  type="checkbox"
                  checked={imenovalecPozitiven}
                  onChange={(e) => {
                    setImenovalecPozitiven(e.target.checked);
                  }}
                />{' '}
                {b.gr.imenovalecPozitiven}
              </label>
              <label className="stikalo">
                <input
                  type="checkbox"
                  checked={skrijNic}
                  onChange={(e) => {
                    setSkrijNic(e.target.checked);
                  }}
                />{' '}
                {b.gr.skrijNic}
              </label>
            </section>

            <section className="graditelj-plosca" aria-labelledby="naslov-predogleda" aria-live="polite">
              <h2 id="naslov-predogleda">
                {b.gr.predogled}
                {podjetja !== null && podjetje !== null && (
                  <span className="drobno"> · {podjetja.find((p) => p.id === podjetje)?.naziv}</span>
                )}
              </h2>
              {zapisFormule === null && formula.napaka === null && <p className="drobno">{b.gr.vpisiFormulo}</p>}
              {racunam && <p className="drobno">{b.gr.racunam}</p>}
              {napakaPredogleda !== null && (
                <p className="napaka" role="alert">
                  {napakaPredogleda}
                </p>
              )}
              {predogled !== null && zapisFormule !== null && (
                <>
                  {predogled.opozorila.length > 0 && (
                    <ul className="opozorila">
                      {predogled.opozorila.map((o) => (
                        <li key={o}>{o}</li>
                      ))}
                    </ul>
                  )}
                  {predogled.kazalnik === null ? (
                    <p className="opozorilo">
                      {b.gr.niPrikazan}
                      {predogled.razlogSkrit !== null && `: ${predogled.razlogSkrit}`}
                    </p>
                  ) : (
                    <IzracunKazalnika
                      kazalnik={predogled.kazalnik}
                      signali={[]}
                      leto={leto ?? 0}
                      primerjalno={primerjalno}
                      naCelico={setCelica}
                    />
                  )}
                </>
              )}
            </section>

            <ShraniKazalnik
              obrazec={obrazec}
              koda={koda}
              urejana={urejana}
              skupine={katalog?.skupine ?? []}
              kodaVeljavna={kodaVeljavna}
              moznoShraniti={zapisFormule !== null && kodaVeljavna && !shranjujem}
              shranjujem={shranjujem}
              // »Shranjeno« velja samo, dokler je formula (z enoto, decimalkami
              // in možnostmi) enaka shranjeni; spremembe obrazca ga počistijo same.
              shranjeno={shranjeno !== null && shranjeno.formula === kljucPredogleda ? shranjeno : null}
              napaka={napakaShranjevanja}
              naSpremembo={spremeni}
              naShrani={shrani}
            />
          </div>

          <section className="graditelj-plosca graditelj-mreza" aria-label={b.gr.formula}>
            {mreza === null || stevilcenje === null ? (
              napaka === null && <p>{podjetja?.length === 0 ? b.gr.brezPodjetij : leta?.length === 0 ? b.gr.brezLet : b.gr.beremMrezo}</p>
            ) : (
              <Mreza
                mreza={mreza}
                stevilcenje={stevilcenje}
                zStolpcemC={zStolpcemC}
                zavihek={zavihek}
                naZavihek={setZavihek}
                iskanje={iskanje}
                naIskanje={setIskanje}
                razprte={razprte}
                naRazpri={(kljuc) => {
                  const nove = new Set(razprte);
                  if (nove.has(kljuc)) nove.delete(kljuc);
                  else nove.add(kljuc);
                  setRazprte(nove);
                }}
                uporabljene={uporabljene}
                naCelico={vpisi}
              />
            )}
          </section>
        </div>
      </div>

      {celica !== null && podjetje !== null && (
        <PanelRazclenitve
          podjetje={podjetje}
          celica={celica}
          naZapri={() => {
            setCelica(null);
          }}
        />
      )}
    </main>
  );
}

interface LastnostiShranjevanja {
  obrazec: Obrazec;
  koda: string;
  urejana: string | null;
  skupine: { skupina: string; naziv: string }[];
  kodaVeljavna: boolean;
  moznoShraniti: boolean;
  shranjujem: boolean;
  shranjeno: { naziv: string; opozorila: string[] } | null;
  napaka: string | null;
  naSpremembo: (s: Partial<Obrazec>) => void;
  naShrani: () => void;
}

/**
 * Shranjevanje v katalog (6.3): naziva in opisa v obeh jezikih, skupina
 * (privzeto »Po meri«, na dnu strani Pregled), koda in splošni vklop. Koda se
 * po shranjevanju ne spremeni. Opozorila formule (npr. stanje, deljeno s
 * stanjem) shranjevanja ne ustavijo, se pa pokažejo.
 */
function ShraniKazalnik(l: LastnostiShranjevanja): JSX.Element {
  const b = useBesedila();
  const o = l.obrazec;
  const polje = (napis: string, vrednost: string, kaj: keyof Obrazec): JSX.Element => (
    <label className="polje">
      {napis}
      <input
        value={vrednost}
        onChange={(e) => {
          l.naSpremembo({ [kaj]: e.target.value });
        }}
      />
    </label>
  );
  const besedilo = (napis: string, vrednost: string, kaj: keyof Obrazec): JSX.Element => (
    <label className="polje">
      {napis}
      <textarea
        rows={2}
        value={vrednost}
        onChange={(e) => {
          l.naSpremembo({ [kaj]: e.target.value });
        }}
      />
    </label>
  );
  return (
    <section className="graditelj-plosca shrani-kazalnik" aria-labelledby="naslov-shranjevanja">
      <h2 id="naslov-shranjevanja">{b.gr.shraniNaslov}</h2>
      <div className="mreza-obrazca">
        {polje(b.gr.nazivSl, o.nazivSl, 'nazivSl')}
        {polje(b.gr.nazivEn, o.nazivEn, 'nazivEn')}
        <label className="polje">
          {b.gr.skupina}
          <select
            value={o.skupina}
            onChange={(e) => {
              l.naSpremembo({ skupina: e.target.value });
            }}
          >
            {l.skupine.map((s) => (
              <option key={s.skupina} value={s.skupina}>
                {s.naziv}
              </option>
            ))}
          </select>
        </label>
        <label className="polje">
          {b.gr.koda}
          <input
            className="mono"
            value={l.koda}
            disabled={l.urejana !== null}
            title={b.gr.kodaNamig}
            aria-invalid={!l.kodaVeljavna}
            onChange={(e) => {
              l.naSpremembo({ koda: e.target.value, kodaRocno: true });
            }}
          />
        </label>
      </div>
      {!l.kodaVeljavna && (
        <p className="napaka" role="alert">
          {b.gr.kodaNeveljavna}
        </p>
      )}
      {besedilo(b.gr.opisSl, o.opisSl, 'opisSl')}
      {besedilo(b.gr.opisEn, o.opisEn, 'opisEn')}
      <label className="stikalo">
        <input
          type="checkbox"
          checked={o.dodajVPregled}
          onChange={(e) => {
            l.naSpremembo({ dodajVPregled: e.target.checked });
          }}
        />{' '}
        {b.gr.dodajVPregled}
      </label>
      {l.napaka !== null && (
        <p className="napaka" role="alert">
          {l.napaka}
        </p>
      )}
      {l.shranjeno !== null && (
        <div role="status">
          <p className="uspeh-besedilo">{b.gr.shranjeno(l.shranjeno.naziv)}</p>
          {l.shranjeno.opozorila.length > 0 && (
            <ul className="opozorila">
              {l.shranjeno.opozorila.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          )}
          <p>
            <Link to="/nastavitve/kazalniki">{b.gr.nazajVKatalog}</Link>
          </p>
        </div>
      )}
      <p className="gumbi">
        <button type="button" className="gumb" onClick={l.naShrani} disabled={!l.moznoShraniti}>
          {l.shranjujem ? b.gr.shranjujem : l.urejana === null ? b.gr.shrani : b.gr.shraniSpremembe}
        </button>
      </p>
    </section>
  );
}

interface LastnostiIzbire {
  podjetja: Podjetje[] | null;
  podjetje: string | null;
  leta: LetoPodatkov[] | null;
  leto: number | null;
  primerjalno: number | null;
  mreza: MrezaGraditelja | null;
  pokaziC: boolean;
  naPodjetje: (id: string) => void;
  naLeto: (leto: number) => void;
  naPrimerjalno: (leto: number) => void;
  naStolpecC: (da: boolean) => void;
}

/** Podjetje in leti predogleda, pomen stolpcev in opozorila mreže. */
function IzbiraPredogleda(l: LastnostiIzbire): JSX.Element {
  const b = useBesedila();
  const letaPodjetja = (l.leta ?? []).map((x) => x.leto).sort((a, c) => c - a);
  // Primerjalno je lahko tudi leto brez knjižb (mreža to pove z opozorilom).
  const primerjalna =
    l.leto === null ? [] : [...new Set([l.leto - 1, ...letaPodjetja.filter((x) => x < l.leto!)])].sort((a, c) => c - a);
  return (
    <section className="graditelj-plosca izbira-predogleda">
      <div className="mreza-obrazca">
        <label className="polje">
          {b.gr.podjetje}
          <select
            value={l.podjetje ?? ''}
            onChange={(e) => {
              l.naPodjetje(e.target.value);
            }}
          >
            {(l.podjetja ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.naziv}
              </option>
            ))}
          </select>
        </label>
        <label className="polje">
          {b.gr.leto}
          <select
            value={l.leto ?? ''}
            onChange={(e) => {
              l.naLeto(Number(e.target.value));
            }}
          >
            {letaPodjetja.map((x) => (
              <option key={x} value={x}>
                {x}
              </option>
            ))}
          </select>
        </label>
        <label className="polje">
          {b.gr.primerjalno}
          <select
            value={l.primerjalno ?? ''}
            onChange={(e) => {
              l.naPrimerjalno(Number(e.target.value));
            }}
          >
            {primerjalna.map((x) => (
              <option key={x} value={x}>
                {x}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="drobno">{b.gr.samoPredogled}</p>
      {l.mreza !== null && (
        <p className="drobno opis-stolpcev">
          {STOLPCI.filter((s) => s.stolpec !== 'C' || l.pokaziC).map((s) => (
            <span key={s.stolpec}>
              <span className={`koord koord-${s.stolpec.toLowerCase()}`}>{s.stolpec}</span> {l.mreza!.stolpci[s.obdobje]}
            </span>
          ))}
        </p>
      )}
      <label className="stikalo">
        <input
          type="checkbox"
          checked={l.pokaziC}
          onChange={(e) => {
            l.naStolpecC(e.target.checked);
          }}
        />{' '}
        {b.gr.stolpecC}
      </label>
      {l.mreza !== null && l.mreza.opozorila.length > 0 && (
        <ul className="opozorila">
          {l.mreza.opozorila.map((o) => (
            <li key={o}>{o}</li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Pod formulo: kaj pomeni vsaka celica (vrstica, različica, obdobje stolpca). */
function PomenCelic({
  uporabljene,
  stevilcenje,
  mreza,
}: {
  uporabljene: { stolpec: Stolpec; vrstica: number }[];
  stevilcenje: Stevilcenje | null;
  mreza: MrezaGraditelja | null;
}): JSX.Element | null {
  const b = useBesedila();
  if (stevilcenje === null || mreza === null || uporabljene.length === 0) return null;
  const videne = new Set<string>();
  const vrstice = uporabljene.flatMap((c) => {
    const koord = `${c.stolpec}${String(c.vrstica)}`;
    if (videne.has(koord)) return [];
    videne.add(koord);
    const najdeno = stevilcenje.poStevilki.get(c.vrstica);
    if (najdeno === undefined) return [];
    const obdobje = STOLPCI.find((s) => s.stolpec === c.stolpec)!.obdobje;
    return [
      <li key={koord}>
        <span className={`koord koord-${c.stolpec.toLowerCase()}`}>{koord}</span> {najdeno.vrstica.vrstica.naziv} ·{' '}
        {najdeno.varianta.naziv} · {mreza.stolpci[obdobje]}
      </li>,
    ];
  });
  return (
    <div className="pomen-formule">
      <h3 className="drobno">{b.gr.pomen}</h3>
      <ul>{vrstice}</ul>
    </div>
  );
}

interface LastnostiMreze {
  mreza: MrezaGraditelja;
  stevilcenje: Stevilcenje;
  zStolpcemC: boolean;
  zavihek: OdsekGraditelja;
  naZavihek: (o: OdsekGraditelja) => void;
  iskanje: string;
  naIskanje: (s: string) => void;
  razprte: Set<string>;
  naRazpri: (kljuc: string) => void;
  uporabljene: { stolpec: Stolpec; vrstica: number }[];
  naCelico: (koordinata: string) => void;
}

/**
 * Mreža: zavihki po odsekih sheme, iskanje po vseh odsekih, vrstica s
 * privzeto različico in razpiranje ostalih. Številke so stalne v seji.
 */
function Mreza(l: LastnostiMreze): JSX.Element {
  const b = useBesedila();
  const stolpci = STOLPCI.filter((s) => s.stolpec !== 'C' || l.zStolpcemC);
  const uporabljeneKoord = new Set(l.uporabljene.map((c) => `${c.stolpec}${String(c.vrstica)}`));
  const uporabljeneSt = new Set(l.uporabljene.map((c) => c.vrstica));
  const iskano = zaIskanje(l.iskanje.trim());
  const vidne =
    iskano === ''
      ? l.stevilcenje.vrstice.filter((v) => v.vrstica.odsek === l.zavihek)
      : l.stevilcenje.vrstice.filter((v) => !v.vrstica.naslov && zaIskanje(v.vrstica.naziv).includes(iskano));

  const celica = (st: number, v: OstevilcenaVrstica, i: number, s: (typeof STOLPCI)[number]): JSX.Element => {
    const vrednost = v.vrstica.variante[i]!.vrednosti[s.obdobje];
    const koord = `${s.stolpec}${String(st)}`;
    return (
      <td key={s.stolpec} className={`celica celica-${s.stolpec.toLowerCase()}`}>
        <button
          type="button"
          className={[vrednost.vrednost === null ? 'prazna' : '', uporabljeneKoord.has(koord) ? 'uporabljena' : '']
            .filter((r) => r !== '')
            .join(' ')}
          title={vrednost.razlog ?? b.gr.vstavi(koord)}
          aria-label={`${koord}: ${vrednost.vrednost === null ? (vrednost.razlog ?? b.pk.ni) : znesek(vrednost.vrednost)}`}
          // Klik z miško formuli ne vzame fokusa: kazalka ostane, kjer je bila.
          onMouseDown={(e) => {
            e.preventDefault();
          }}
          onClick={() => {
            l.naCelico(koord);
          }}
        >
          {vrednost.vrednost === null ? b.pk.ni : znesek(vrednost.vrednost)}
        </button>
      </td>
    );
  };

  const vrstice: JSX.Element[] = [];
  let odsek: OdsekGraditelja | null = null;
  for (const v of vidne) {
    if (iskano !== '' && v.vrstica.odsek !== odsek) {
      odsek = v.vrstica.odsek;
      vrstice.push(
        <tr key={`odsek-${odsek}`} className="odsek">
          <td colSpan={2 + stolpci.length}>{b.gr.odseki[odsek]}</td>
        </tr>,
      );
    }
    const zamik = { paddingLeft: `${String(v.vrstica.raven * 1.1)}rem` };
    if (v.vrstica.naslov === true || v.stevilke.length === 0) {
      vrstice.push(
        <tr key={v.kljuc} className={v.vrstica.naslov === true ? 'naslov' : 'posebnost'}>
          <td className="st" />
          <td style={zamik}>
            {v.vrstica.naziv}
            {v.vrstica.posebnost === true && <span className="drobno"> – {b.gr.posebnost}</span>}
          </td>
          {stolpci.map((s) => (
            <td key={s.stolpec} />
          ))}
        </tr>,
      );
      continue;
    }
    // Vrstica z različico v formuli je razprta, da je celica vidna.
    const razprta = l.razprte.has(v.kljuc) || v.stevilke.slice(1).some((st) => uporabljeneSt.has(st));
    const vec = v.stevilke.length > 1;
    v.stevilke.forEach((st, i) => {
      if (i > 0 && !razprta) return;
      const varianta = v.vrstica.variante[i]!;
      vrstice.push(
        <tr key={`${v.kljuc}-${String(i)}`} className={[i === 0 ? 'glavna' : 'varianta', uporabljeneSt.has(st) ? 'v-formuli' : ''].join(' ')}>
          <td className="st">{st}</td>
          <td style={i === 0 ? zamik : { paddingLeft: `${String(v.vrstica.raven * 1.1 + 1.6)}rem` }}>
            {i === 0 ? (
              <span className="naziv-vrstice">
                {vec ? (
                  <button
                    type="button"
                    className="razpri"
                    aria-expanded={razprta}
                    aria-label={b.gr.razpri(v.vrstica.naziv)}
                    onClick={() => {
                      l.naRazpri(v.kljuc);
                    }}
                  >
                    {razprta ? '▾' : '▸'}
                  </button>
                ) : (
                  <span className="razpri-prostor" />
                )}
                <span>
                  {v.vrstica.naziv} <span className="drobno">· {varianta.naziv}</span>
                  {v.vrstica.vloga !== null && <span className="vloga-vrstice">{v.vrstica.vloga}</span>}
                </span>
              </span>
            ) : (
              <span className="drobno">{varianta.naziv}</span>
            )}
          </td>
          {stolpci.map((s) => celica(st, v, i, s))}
        </tr>,
      );
    });
  }

  return (
    <>
      <div className="mreza-orodja">
        <input
          type="search"
          value={l.iskanje}
          placeholder={b.gr.iskanje}
          aria-label={b.gr.iskanje}
          onChange={(e) => {
            l.naIskanje(e.target.value);
          }}
        />
        <p className="drobno">{b.gr.namigMreze}</p>
      </div>
      {iskano === '' && (
        <div className="zavihki" role="tablist">
          {ODSEKI.filter((o) => l.mreza.vrstice.some((v) => v.odsek === o)).map((o) => (
            <button
              key={o}
              type="button"
              role="tab"
              aria-selected={l.zavihek === o}
              onClick={() => {
                l.naZavihek(o);
              }}
            >
              {b.gr.odseki[o]}
            </button>
          ))}
        </div>
      )}
      <div className="mreza-ovoj">
        <table className="mreza-graditelja">
          <thead>
            <tr>
              <th className="st">#</th>
              <th />
              {stolpci.map((s) => (
                <th key={s.stolpec} className={`stevilka stolpec-${s.stolpec.toLowerCase()}`}>
                  {s.stolpec}
                  <span className="namig-stolpca">{l.mreza.stolpci[s.obdobje]}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{vrstice}</tbody>
        </table>
      </div>
    </>
  );
}
